/**
 * 중계기 설정 (BLE 프로비저닝)
 *
 * monitor 앱(220)의 RepeaterSetupPanel 을 관리자웹 화면으로 옮긴 것.
 * adminweb 은 웹 전용이라 react-native-web 위에서 DOM 요소를 그대로 쓴다
 * (Analysis.tsx 등 기존 화면과 같은 방식).
 *
 * Web Bluetooth 는 HTTPS + Chrome/Edge 에서만 동작한다.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigation } from '@react-navigation/native';
import { getStorage } from '../storage';
import { useDarkMode } from './common';
import Data from './Data';

// 다른 화면(Repeater.tsx 등)과 같은 팔레트. 라이트/다크 모드를 따라간다.
const RC_DARK = {
  card: '#1E293B', cardInner: '#0F172A', border: 'rgba(99,179,237,0.12)',
  text: '#E2E8F0', textSub: '#94A3B8', muted: '#a8adb497',
};
const RC_LIGHT = {
  card: '#FFFFFF', cardInner: '#F3F4F6', border: '#E5E7EB',
  text: '#111827', textSub: '#4B5563', muted: '#9CA3AF',
};
const RC_ACCENT = '#38BDF8';

const RS_CSS = `
/* 추출 과정에서 누락됐던 규칙들 — 스피너 회전이 이것 없이는 멈춰 보인다 */
@keyframes rs-spin { to { transform: rotate(360deg); } }
.rs-label-mini { font-size: 12px; color: var(--text-sub); font-weight: 600; }
.rs-same-server { color: #16A34A; font-size: 12px; margin-top: 6px; font-weight: 600; }
.rs-diff-server { color: #D97706; font-size: 12px; margin-top: 6px; font-weight: 600; }
.rs-btn-inline {
  margin-left: 8px; padding: 3px 10px; font-size: 12px; font-weight: 600;
  border: 1px solid var(--accent); background: transparent; color: var(--accent);
  border-radius: 6px; cursor: pointer;
}
.repeater-setup-panel {
  --accent: #38BDF8;
  --text-sub: #94A3B8;
  --border: rgba(148,163,184,0.25);
}
/* ===== Repeater Setup Panel ===== */
.repeater-setup-panel {
  max-width: 720px; margin: 24px auto; padding: 0 16px;
  font-family: inherit; color: var(--text, #e0e0e0);
}
.rs-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 16px;
}
.rs-header h2 { margin: 0; font-size: 22px; }
.rs-phase {
  padding: 4px 10px; border-radius: 12px; font-size: 12px;
  background: var(--bg-card, #2a2a2a); color: var(--muted, #888);
}
.rs-phase-success { background: #1f5e3a; color: #fff; }
.rs-phase-failed { background: #5c2a2a; color: #fff; }
.rs-phase-sending, .rs-phase-scanning, .rs-phase-connecting { background: #2c4a7a; color: #fff; }
.rs-card {
  background: var(--bg-card, #1f1f1f); border: 1px solid var(--border, #333);
  border-radius: 10px; padding: 18px; margin-bottom: 14px;
}
.rs-card p { margin: 6px 0; color: var(--muted, #aaa); }
.rs-steps { padding-left: 18px; margin: 12px 0; color: var(--muted, #bbb); }
.rs-steps li { margin: 4px 0; font-size: 14px; }
.rs-device-info {
  background: var(--bg, #181818); padding: 10px 12px; border-radius: 6px;
  margin-bottom: 14px; font-size: 13px;
}
.rs-device-info span { color: var(--muted, #888); margin-left: 6px; }
.rs-section { margin-bottom: 14px; }
.rs-section label {
  display: block; font-size: 13px; color: var(--muted, #aaa); margin-bottom: 6px;
}
.rs-row-between { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.rs-ssid-list { display: flex; flex-direction: column; gap: 6px; max-height: 220px; overflow-y: auto; }
.rs-ssid-btn {
  text-align: left; padding: 10px 12px;
  background: var(--bg, #181818); color: var(--text, #e0e0e0);
  border: 1px solid var(--border, #333); border-radius: 6px;
  cursor: pointer; font-size: 14px;
}
.rs-ssid-btn:hover { border-color: var(--accent, #5a8de8); }
.rs-ssid-sel { background: var(--accent, #5a8de8); color: #fff; border-color: var(--accent, #5a8de8); }
.rs-input {
  width: 100%; padding: 10px 12px; box-sizing: border-box;
  background: var(--bg, #181818); color: var(--text, #e0e0e0);
  border: 1px solid var(--border, #333); border-radius: 6px;
  font-size: 14px; margin-top: 8px;
}
.rs-input:focus { outline: none; border-color: var(--accent, #5a8de8); }
.rs-error {
  background: #5c2a2a; color: #fff; padding: 10px 12px; border-radius: 6px;
  margin: 10px 0; font-size: 13px;
}
.rs-actions { display: flex; gap: 10px; margin-top: 16px; }
.rs-btn {
  flex: 1; padding: 12px 16px; border-radius: 6px; font-size: 14px;
  border: 1px solid var(--border, #333); background: var(--bg, #181818);
  color: var(--text, #e0e0e0); cursor: pointer;
}
.rs-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.rs-btn-primary { background: var(--accent, #5a8de8); color: #fff; border-color: var(--accent, #5a8de8); }
.rs-btn-mini {
  padding: 4px 10px; font-size: 12px; border-radius: 4px;
  background: var(--bg, #181818); color: var(--text, #e0e0e0);
  border: 1px solid var(--border, #333); cursor: pointer;
}
.rs-loading, .rs-ok, .rs-err { text-align: center; padding: 32px 18px; }
.rs-loading p, .rs-ok p, .rs-err p { color: var(--muted, #bbb); }
.rs-big-icon { font-size: 64px; margin-bottom: 12px; }
.rs-ok .rs-big-icon { color: #4ade80; }
.rs-err .rs-big-icon { color: #f87171; }
.rs-spinner, .rs-spinner-small {
  border: 3px solid var(--border, #333); border-top-color: var(--accent, #5a8de8);
  border-radius: 50%; animation: rs-spin 0.8s linear infinite;
  margin: 12px auto;
}
.rs-spinner { width: 36px; height: 36px; }
.rs-spinner-small { width: 22px; height: 22px; }
.repeater-setup-panel {
    max-width: 100%; margin: 12px 0; padding: 0 10px;
  }
.rs-header { flex-wrap: wrap; gap: 8px; }
.rs-header h2 { font-size: 18px; }
.rs-phase { font-size: 11px; padding: 3px 8px; }
.rs-card { padding: 14px; border-radius: 8px; margin-bottom: 10px; }
.rs-steps li { font-size: 13px; }
.rs-device-info { font-size: 12px; padding: 8px 10px; }
.rs-device-info span { display: block; margin-left: 0; margin-top: 2px; }
.rs-ssid-list { max-height: 280px; }
.rs-ssid-btn { padding: 14px 12px; font-size: 15px; min-height: 48px; }
.rs-input { padding: 14px; font-size: 16px; min-height: 48px; }
.rs-actions { flex-direction: column-reverse; gap: 8px; }
.rs-btn { padding: 14px 16px; font-size: 15px; min-height: 50px; }
.rs-btn-primary { font-weight: 600; }
.rs-loading, .rs-ok, .rs-err { padding: 24px 14px; }
.rs-big-icon { font-size: 56px; }
.rs-header h2 { font-size: 16px; }
.rs-card { padding: 12px; }
.rs-ssid-btn { font-size: 14px; }
/* 비밀번호 보기 토글 (React 패널) */
.rs-pw-wrap { position: relative; }
.rs-pw-wrap .rs-input { padding-right: 50px; }
.rs-pw-toggle {
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
  width: 38px; height: 38px; border: 0; background: transparent; cursor: pointer;
  font-size: 18px; color: var(--muted, #888);
}
.rs-pw-toggle.on { color: var(--accent, #5a8de8); }
/* SSID 선택 강조 (React 패널) */
.rs-ssid-btn { transition: all 0.15s; font-weight: 500; }
.rs-ssid-sel {
  border-width: 2px !important; padding: 9px 11px;
  box-shadow: 0 4px 16px rgba(91, 141, 239, 0.4);
  font-weight: 700;
}
.rs-ssid-btn:active { transform: scale(0.98); }
/* ===== Repeater Setup — site theme integration (overrides earlier defaults) ===== */
.repeater-setup-panel { color: var(--text); }
.rs-header h2 { color: var(--text); }
.rs-phase { background: var(--card); color: var(--text-sub); border: 1px solid var(--border); }
.rs-card { background: var(--card); border-color: var(--border); }
.rs-card p { color: var(--text-sub); }
.rs-steps { color: var(--text-sub); }
.rs-device-info { background: var(--card-hover); border: 1px solid var(--border); }
.rs-device-info span { color: var(--text-sub); }
.rs-section label { color: var(--text-sub); }
.rs-ssid-btn { background: var(--card-hover); color: var(--text); border-color: var(--border); }
.rs-ssid-btn:hover { border-color: var(--accent); }
.rs-ssid-sel { background: var(--accent); color: #fff; border-color: var(--accent); }
.rs-input { background: var(--card-hover); color: var(--text); border-color: var(--border); }
.rs-input::placeholder { color: var(--text-sub); }
.rs-input:focus { border-color: var(--accent); }
.rs-btn { background: var(--card-hover); color: var(--text); border-color: var(--border); }
.rs-btn:hover { border-color: var(--accent); }
.rs-btn-primary { background: var(--accent); color: #fff; border-color: var(--accent); }
.rs-btn-mini { background: var(--card-hover); color: var(--text); border-color: var(--border); }
.rs-pw-toggle { color: var(--text-sub); }
.rs-pw-toggle.on { color: var(--accent); }
.rs-loading p, .rs-ok p, .rs-err p { color: var(--text-sub); }
.rs-spinner { border-color: var(--border); border-top-color: var(--accent); }
.rs-spinner-small { border-color: var(--border); border-top-color: var(--accent); }
.rs-phase-success { background: rgba(56, 189, 168, 0.18); color: var(--accent); }
.rs-phase-failed { background: rgba(248, 113, 113, 0.18); color: #f87171; }
.rs-phase-sending, .rs-phase-scanning, .rs-phase-connecting { background: rgba(56, 189, 248, 0.18); color: var(--accent); }
`;


const BLE_SVC_UUID    = '5e4d4544-494c-4f47-4953-000000000001';
const BLE_CHAR_INFO   = '5e4d4544-494c-4f47-4953-000000000002';
const BLE_CHAR_SCAN   = '5e4d4544-494c-4f47-4953-000000000003';
const BLE_CHAR_CFG    = '5e4d4544-494c-4f47-4953-000000000004';
const BLE_CHAR_STATUS = '5e4d4544-494c-4f47-4953-000000000005';
const BLE_CHAR_AUTH   = '5e4d4544-494c-4f47-4953-000000000006';

// WIFI QR 파싱: WIFI:T:WPA;S:ssid;P:pw;;
function parseWifiQr(text) {
  if (!text || !text.startsWith('WIFI:')) return null;
  const out = {};
  for (const p of text.slice(5).split(';')) {
    if (!p) continue;
    const i = p.indexOf(':');
    if (i < 0) continue;
    const k = p.slice(0, i), v = p.slice(i + 1);
    if (k === 'S') out.ssid = v;
    else if (k === 'P') out.password = v;
    else if (k === 'T') out.type = v;
  }
  return out.ssid ? out : null;
}

// RSSI에 따라 WiFi 신호 아이콘 (5단계)
function WifiIcon({ level }) {
  const arcs = [
    { d: 'M2 6 Q10 -2 18 6',          idx: 4 },
    { d: 'M4.5 8.5 Q10 2.5 15.5 8.5', idx: 3 },
    { d: 'M7 11 Q10 7 13 11',         idx: 2 },
  ];
  const dim = 'rgba(140,140,140,0.4)';
  return (
    <svg width="20" height="16" viewBox="0 0 20 16" fill="none" style={{flexShrink:0}}>
      {arcs.map((a, i) => (
        <path key={i} d={a.d} stroke={level >= a.idx ? 'currentColor' : dim} strokeWidth="3" strokeLinecap="round" fill="none"/>
      ))}
      <circle cx="10" cy="13" r="1.5" fill={level >= 1 ? 'currentColor' : dim}/>
    </svg>
  );
}
function rssiToLevel(rssi) {
  if (rssi >= -70) return 4;
  if (rssi >= -80) return 3;
  if (rssi >= -90) return 2;
  if (rssi >= -100) return 1;
  return 0;
}

const STATUS_CODE_MAP = {
  '201': 'WiFi 이름(SSID)을 찾을 수 없습니다',
  '202': '비밀번호가 틀렸습니다',
  '203': 'WiFi 연결 실패 (라우터 문제 가능)',
  '204': '신호 끊김 (Beacon timeout)',
  '205': '연결 실패 (Association)',
  '206': '인증 시간 초과 (Handshake)',
};

/**
 * 지금 이 관리자웹이 붙어 있는 서버의 게이트웨이 주소.
 * 중계기는 gwserver 로 데이터를 보내므로 adminserver 포트가 아니라 그쪽을 가리켜야 한다.
 * 사이트마다 포트가 달라 window.location 에서 유추하고, 필요하면 사용자가 고칠 수 있게 둔다.
 */
function currentGatewayUrl() {
  try {
    const { hostname } = window.location;
    // 중계기 펌웨어는 http://IP:포트 만 처리한다(도메인·HTTPS 불가).
    // 그래서 IP 로 접속한 경우에만 자동 계산하고, 포트는 빌드 때 주입된 값을 쓴다.
    const port = (typeof process !== 'undefined' && process.env && process.env.GW_PORT) || '';
    const isIp = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
    if (isIp && port) {
      return 'http://' + hostname + ':' + port;
    }
    return '';
  } catch (e) {
    return '';
  }
}

/**
 * IP 로 접속한 경우의 보조 판단.
 * 서버가 게이트웨이 외부 주소를 갖고 있지 않아 확정할 수 없으므로,
 * '같은 호스트를 보고 있는지'까지만 본다. 포트는 사이트마다 달라 비교하지 않는다.
 */
function sameHostAsHere(url) {
  try {
    const here = window.location.hostname;
    const there = new URL(url).hostname;
    return here && there && here === there;
  } catch (e) {
    return false;
  }
}

/** 비교용 정규화 — 끝 슬래시·대소문자 차이로 다르게 보이지 않게 */
function normUrl(u) {
  return String(u || '').trim().replace(/\/+$/, '').toLowerCase();
}

/** 약속이 제한 시간 안에 끝나지 않으면 실패로 돌린다(무한 대기 방지). */
function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('연결 시간이 초과되었습니다')), ms)),
  ]);
}

function RepeaterSetupPanel({ onClose }) {
  const [phase, setPhase] = useState('idle'); // idle | connecting | scanning | ready | sending | success | failed
  const [device, setDevice] = useState(null);
  const [info, setInfo] = useState(null);
  const [ssids, setSsids] = useState([]);
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  // 서버 주소. 비워 두면 전송하지 않아 중계기의 기존 설정이 유지된다.
  const [server, setServer] = useState('');
  const [serverOrig, setServerOrig] = useState('');
  // 서버가 알려준 게이트웨이 공개 IP. 포트와 합쳐 '현재 서버' 주소를 만든다.
  const [gwFromServer, setGwFromServer] = useState('');

  // 서버 등록 상태 — null: 조회 전, false: 미등록, 객체: 등록된 행
  const [reg, setReg] = useState(null);
  const [regChecking, setRegChecking] = useState(false);
  const [areas, setAreas] = useState([]);
  const [regArea, setRegArea] = useState('');
  const [regRoom, setRegRoom] = useState('');
  const [regBusy, setRegBusy] = useState(false);
  const [regMsg, setRegMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await getStorage('Session');
        const ip = s && s.gwIp;
        const port = (typeof process !== 'undefined' && process.env && process.env.GW_PORT) || '';
        if (ip && port) setGwFromServer('http://' + ip + ':' + port);
      } catch (e) { /* 없으면 접속 주소로 유추 */ }
    })();
  }, []);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);   // 진행 안내(에러 아님)
  const [showPw, setShowPw] = useState(true);
  const [countdown, setCountdown] = useState(0);

  const charsRef = useRef({});
  const deviceRef = useRef(null);   // 정리 시점에 최신 값을 확실히 잡기 위한 ref
  const statusRef = useRef(null);
  const completedRef = useRef(false);
  const phaseRef = useRef('idle');

  // setPhase wrapper — completed 상태에선 failed로 절대 못 바뀜
  const setPhaseSafe = (p) => {
    if (completedRef.current && p === 'failed') {
      console.log('[BLE] blocked setPhase(failed) after ok');
      return;
    }
    setPhase(p);
  };

  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  const supported = typeof navigator !== 'undefined' && navigator.bluetooth;

  const toUtf8 = (s) => new TextEncoder().encode(s);
  const fromUtf8 = (buf) => new TextDecoder().decode(buf);

  /**
   * GATT 연결을 타임아웃과 함께 시도한다.
   * 직전 연결이 아직 안 끊긴 중계기는 응답을 주지 않아 그대로 두면 무한 대기가 된다.
   * 링크 타임아웃이 풀릴 시간을 두고 몇 번 다시 시도한다.
   */
  const connectWithRetry = async (dev, tries = 3) => {
    let lastErr = null;
    for (let i = 0; i < tries; i++) {
      try {
        await withTimeout(setupGattAndNotify(dev), 9000);
        setNotice(null);          // 붙었으면 진행 안내를 지운다
        return;
      } catch (e) {
        lastErr = e;
        try { if (dev?.gatt?.connected) dev.gatt.disconnect(); } catch {}
        if (i < tries - 1) {
          setNotice(`연결 재시도 중... (${i + 2}/${tries})`);
          await new Promise(r => setTimeout(r, 2500));
        }
      }
    }
    throw lastErr || new Error('연결 실패');
  };

  const setupGattAndNotify = async (dev) => {
    dev.addEventListener('gattserverdisconnected', onDisconnected);
    const server = await dev.gatt.connect();
    const svc = await server.getPrimaryService(BLE_SVC_UUID);
    const [cInfo, cScan, cCfg, cStatus] = await Promise.all([
      svc.getCharacteristic(BLE_CHAR_INFO),
      svc.getCharacteristic(BLE_CHAR_SCAN),
      svc.getCharacteristic(BLE_CHAR_CFG),
      svc.getCharacteristic(BLE_CHAR_STATUS),
    ]);
    // 인증 특성은 구버전 펌웨어에 없다. 없으면 서버주소 변경만 막고 나머지는 그대로 쓴다.
    let cAuth = null;
    try { cAuth = await svc.getCharacteristic(BLE_CHAR_AUTH); } catch (e) { cAuth = null; }
    charsRef.current = { cInfo, cScan, cCfg, cStatus, cAuth };

    // DEVICE_INFO 읽기
    try {
      const infoVal = await cInfo.readValue();
      const txt = fromUtf8(infoVal.buffer);
      try {
        const parsed = JSON.parse(txt);
        setInfo(parsed);
        // 구버전 펌웨어는 server 를 안 실어보낸다 → 그 경우 입력칸은 빈 채로 둔다
        if (parsed && parsed.server) {
          setServer(parsed.server);
          setServerOrig(parsed.server);
        }
        if (parsed && parsed.mac) {
          checkRegistered(parsed.mac);
          loadAreas();
        }
      } catch { setInfo({ raw: txt }); }
    } catch (e) {
      console.warn('info read fail:', e);
    }

    // status notify
    cStatus.addEventListener('characteristicvaluechanged', (e) => {
      const s = fromUtf8(e.target.value.buffer);
      console.log('CONN_STATUS:', s);
      statusRef.current = s;
      setStatus(s);
      if (s === 'ok') {
        completedRef.current = true;
        setError(null);  // 이전 에러 강제 클리어
        setPhase('success');
        // BLE 즉시 종료 (펌웨어 reset 기다리지 않음)
        try { if (dev?.gatt?.connected) dev.gatt.disconnect(); } catch {}
        // 2초 카운트다운 후 idle
        setCountdown(2);
        let r = 2;
        const t = setInterval(() => {
          r -= 1; setCountdown(Math.max(r, 0));
          if (r <= 0) { clearInterval(t); disconnect(); }
        }, 1000);
      } else if (s.startsWith('fail')) {
        if (completedRef.current) return;
        const code = s.split(':')[1] || '';
        setError(STATUS_CODE_MAP[code] || `WiFi 연결 실패 (${code})`);
        setPhaseSafe('failed');
      } else if (s === 'connecting') {
        setPhase('sending');
      }
    });
    await cStatus.startNotifications();

    // scan notify
    cScan.addEventListener('characteristicvaluechanged', (e) => {
      const txt = fromUtf8(e.target.value.buffer);
      try {
        const list = JSON.parse(txt);
        setSsids(Array.isArray(list) ? list : []);
        setPhase('ready');
      } catch (err) {
        console.warn('scan parse fail:', err);
      }
    });
    await cScan.startNotifications();
  };

  const connect = async () => {
    setError(null);
    setNotice(null);
    setSsid('');
    setPassword('');
    setSsids([]);
    setPhase('connecting');
    try {
      const dev = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix: 'MEDI-' }], optionalServices: [BLE_SVC_UUID],
      });
      setDevice(dev);
      deviceRef.current = dev;
      await connectWithRetry(dev);

      // 자동으로 wifi 스캔 트리거
      setPhase('scanning');
      await charsRef.current.cScan.writeValueWithResponse(toUtf8('1'));
    } catch (e) {
      if (completedRef.current) return;
      console.error(e);
      const msg = String(e && e.message || e);
      setError(msg.indexOf('시간이 초과') !== -1
        ? '중계기가 응답하지 않습니다. 직전 연결이 아직 끊기지 않았을 수 있으니 10초쯤 뒤에 다시 시도하거나, 중계기 전원을 껐다 켜 주세요.'
        : msg);
      setPhaseSafe('failed');
    }
  };

  const reconnect = async () => {
    if (!device) {
      setError('디바이스 정보가 없습니다. "처음부터"로 다시 연결해주세요.');
      setPhaseSafe('failed');
      return;
    }
    setError(null);
    setStatus(null);
    setPhase('connecting');
    try {
      // 기존 GATT 정리 (zombie connection 회피)
      if (device.gatt?.connected) {
        try { device.gatt.disconnect(); } catch {}
      }
      charsRef.current = {};
      await new Promise(r => setTimeout(r, 600));
      await setupGattAndNotify(device);
      setPhase('ready');  // 비번 수정 후 재전송 가능
    } catch (e) {
      console.error('reconnect err:', e);
      setError('재연결 실패: ' + (e.message || e));
      setPhaseSafe('failed');
    }
  };

  const triggerScan = async () => {
    if (!charsRef.current.cScan) return;
    setError(null);
    setSsids([]);
    setPhase('scanning');
    try {
      await charsRef.current.cScan.writeValueWithResponse(toUtf8('1'));
    } catch (e) {
      setError('스캔 실패: ' + e.message);
      setPhase('ready');
    }
  };

  /** BLE 로 받은 MAC 이 서버에 등록돼 있는지 확인한다. */
  const checkRegistered = (mac) => {
    if (!mac) return;
    setRegChecking(true);
    setRegMsg(null);
    // AREA 를 빈 문자열로 주면 전체 목록이 온다(서버가 그렇게 분기한다)
    Data.fetchData('repeater/getRepeatersList', { AREA: '' }, (result, count) => {
      setRegChecking(false);
      const list = Array.isArray(result) ? result : [];
      const target = String(mac).toUpperCase();
      const found = list.find((r) => String(r.ID || '').toUpperCase() === target);
      setReg(found || false);
    }, true);
  };

  /** 등록 화면에서 고를 구역 목록. */
  const loadAreas = () => {
    Data.fetchData('group/getGroupList', {}, (result, count) => {
      const list = Array.isArray(result) ? result : [];
      const uniq = [];
      list.forEach((g) => {
        const a = g.AREA;
        if (a && a !== '로밍' && uniq.indexOf(a) === -1) uniq.push(a);
      });
      setAreas(uniq);
      if (uniq.length && !regArea) setRegArea(uniq[0]);
    }, true);
  };

  const registerRepeater = () => {
    const mac = info && info.mac;
    if (!mac) { setRegMsg('중계기 정보를 읽지 못했습니다.'); return; }
    if (!regArea) { setRegMsg('구역을 선택해주세요.'); return; }
    if (!regRoom.trim()) { setRegMsg('방번호를 입력해주세요.'); return; }
    setRegBusy(true);
    setRegMsg(null);
    Data.fetchData('tabRepeater/insertNewRepeater', {
      ID: mac,
      ROOM_NO: regRoom.trim(),
      AREA: regArea,
      IPV4: '0.0.0.0',        // 예전 VPN 시절 값. 지금은 쓰지 않는다.
    }, (result, count) => {
      setRegBusy(false);
      if (count === 1) {
        setRegMsg(null);
        checkRegistered(mac);
      } else {
        setRegMsg((result && result.message) || '등록에 실패했습니다. 방번호가 중복인지 확인해주세요.');
      }
    }, true);
  };

  /**
   * 서버주소를 바꾸기 전에 중계기에 '우리가 보낸 것' 임을 증명한다.
   * 중계기가 낸 난수를 서버로 보내 서명을 받아 그대로 써 넣는다(비밀키는 서버에만 있다).
   */
  const authorize = async () => {
    const cAuth = charsRef.current && charsRef.current.cAuth;
    if (!cAuth) throw new Error('이 중계기의 펌웨어는 서버주소 변경을 지원하지 않습니다. 펌웨어를 갱신해주세요.');

    const nonceVal = await cAuth.readValue();
    const nonce = fromUtf8(nonceVal.buffer).trim();
    if (!nonce) throw new Error('중계기에서 인증값을 받지 못했습니다.');

    const sig = await new Promise((resolve, reject) => {
      Data.fetchData('repeater/bleSign', { NONCE: nonce }, (result, count) => {
        if (count === 1 && result && result.SIGNATURE) resolve(result.SIGNATURE);
        else reject(new Error((result && result.message) || '서버에서 인증을 받지 못했습니다.'));
      }, true);
    });

    await cAuth.writeValueWithResponse(toUtf8(sig));
    // 중계기가 auth:ok / auth:fail 을 알려준다
    const ok = await new Promise((resolve) => {
      let done = false;
      const t = setTimeout(() => { if (!done) { done = true; resolve(statusRef.current === 'auth:ok'); } }, 3000);
      const iv = setInterval(() => {
        const st = statusRef.current;
        if (!done && (st === 'auth:ok' || st === 'auth:fail')) {
          done = true; clearInterval(iv); clearTimeout(t); resolve(st === 'auth:ok');
        }
      }, 100);
    });
    if (!ok) throw new Error('중계기가 인증을 거부했습니다.');
  };

  const sendConfig = async () => {
    if (!ssid) { setError('SSID를 선택하거나 입력해주세요.'); return; }
    if (!charsRef.current.cCfg) return;

    const srv = server.trim().replace(/\/+$/, '');
    if (srv && !/^https?:\/\//.test(srv)) {
      setError('서버 주소는 http:// 또는 https:// 로 시작해야 합니다.');
      return;
    }
    setError(null);
    setStatus(null);
    setPhase('sending');
    try {
      // 값이 없거나 기존과 같으면 보내지 않는다(중계기가 기존 설정을 유지).
      const cfg = { ssid, password };
      const changingServer = !!(srv && srv !== serverOrig);
      // 서버주소를 바꿀 때만 인증한다. WiFi 만 바꾸면 예전처럼 바로 진행된다.
      if (changingServer) {
        setNotice('서버주소 변경 권한 확인 중...');
        await authorize();
        setNotice(null);
        cfg.server = srv;
      }
      const payload = toUtf8(JSON.stringify(cfg));
      const CHUNK = 20;
      const sleep = (ms) => new Promise(r => setTimeout(r, ms));
      let lastErr = null;
      for (let i = 0; i < payload.length; i += CHUNK) {
        if (i > 0) await sleep(80);
        // 매 청크 직전 cCfg 재확인 — race condition (disconnect 후 빠른 재연결) 회피
        if (!charsRef.current?.cCfg) {
          console.log('[BLE] sendConfig aborted — cCfg cleared mid-loop');
          return;  // 조용히 종료, 에러 표시 X
        }
        const slice = payload.slice(i, Math.min(i + CHUNK, payload.length));
        let tries = 0;
        while (tries < 2) {
          try {
            await charsRef.current.cCfg.writeValueWithResponse(slice);
            lastErr = null;
            break;
          } catch (e) {
            lastErr = e;
            tries += 1;
            if (tries >= 2) break;
            await sleep(150);
          }
        }
        if (completedRef.current) break;
      }
      // 청크 다 보냈는데 에러가 있더라도 — status notify 기다림 (펌웨어가 이미 처리 중일 수 있음)
      // 25초 내에 status 안 오면 그때 timeout 에러
      if (lastErr) {
        console.warn('[BLE] chunk error, waiting for status notify:', lastErr.message);
        await new Promise((resolve) => {
          const t = setTimeout(() => resolve('timeout'), 25000);
          const orig = setStatus;
          // status가 ok/fail로 바뀌면 resolve — completed 또는 phase 'failed' 감지
          const check = setInterval(() => {
            if (completedRef.current || phaseRef.current === 'failed') {
              clearTimeout(t); clearInterval(check); resolve('done');
            }
          }, 200);
        });
        if (!completedRef.current) {
          setError('전송 실패: ' + lastErr.message);
          setPhaseSafe('failed');
        }
      }
    } catch (e) {
      if (completedRef.current) return;
      // 재연결 race condition 에러 — 조용히 무시
      const msg = e.message || '';
      if (msg.includes('undefined') || msg.includes('null')) {
        console.log('[BLE] sendConfig race err ignored:', msg);
        return;
      }
      setError('전송 실패: ' + msg);
      setPhaseSafe('failed');
    }
  };

  
  // ===== WiFi QR 스캔 =====
  const [qrOpen, setQrOpen] = useState(false);
  const [qrHint, setQrHint] = useState('');
  const qrVideoRef = useRef(null);
  const qrCanvasRef = useRef(null);
  const qrStreamRef = useRef(null);
  const qrRafRef = useRef(null);

  // 화면을 벗어나거나 새로고침할 때 BLE 를 확실히 끊는다.
  // 링크가 남으면 중계기(ESP32, 동시 1연결)가 다음 연결을 거부해 '연결 중'에서 멈춘다.
  useEffect(() => {
    const cut = () => {
      try {
        const d = deviceRef.current;
        if (d && d.gatt && d.gatt.connected) d.gatt.disconnect();
      } catch (e) { /* 페이지 종료 중이므로 무시 */ }
    };
    window.addEventListener('beforeunload', cut);
    window.addEventListener('pagehide', cut);
    return () => {
      window.removeEventListener('beforeunload', cut);
      window.removeEventListener('pagehide', cut);
      cut();
    };
  }, []);

  useEffect(() => {
    // jsQR CDN 동적 로딩 (한 번만)
    if (window.jsQR) return;
    const sc = document.createElement('script');
    sc.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';
    sc.async = true;
    document.head.appendChild(sc);
  }, []);

  const stopQrScan = () => {
    if (qrRafRef.current) { cancelAnimationFrame(qrRafRef.current); qrRafRef.current = null; }
    if (qrStreamRef.current) {
      qrStreamRef.current.getTracks().forEach(t => t.stop());
      qrStreamRef.current = null;
    }
    if (qrVideoRef.current) qrVideoRef.current.srcObject = null;
    setQrOpen(false);
  };

  const startQrScan = async () => {
    setQrOpen(true);
    setQrHint('카메라 권한 요청 중...');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 } },
        audio: false,
      });
      qrStreamRef.current = stream;
      const v = qrVideoRef.current;
      if (!v) return;
      v.srcObject = stream;
      await v.play();
      setQrHint('스캔 대기 중...');
      const canvas = qrCanvasRef.current;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      const tick = () => {
        if (!qrStreamRef.current) return;
        if (v.readyState === v.HAVE_ENOUGH_DATA) {
          canvas.width = v.videoWidth;
          canvas.height = v.videoHeight;
          ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
          const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
          if (window.jsQR) {
            const code = window.jsQR(img.data, img.width, img.height, { inversionAttempts: 'attemptBoth' });
            if (code && code.data) {
              const wifi = parseWifiQr(code.data);
              if (wifi) {
                setSsid(wifi.ssid);
                setPassword(wifi.password || '');
                stopQrScan();
                return;
              } else {
                setQrHint('WiFi QR이 아닙니다: ' + code.data.slice(0, 30) + '...');
              }
            }
          }
        }
        qrRafRef.current = requestAnimationFrame(tick);
      };
      qrRafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      setQrHint('카메라 실패: ' + (e.message || e));
    }
  };

  const onDisconnected = () => {
    if (completedRef.current || statusRef.current === 'ok') {
      console.log('[BLE] disconnected (normal after ok)');
      return;
    }
    console.log('[BLE] disconnected (unexpected)');
  };

  const disconnect = () => {
    try {
      if (device?.gatt?.connected) device.gatt.disconnect();
    } catch {}
    setDevice(null);
    setInfo(null);
    setSsids([]);
    setSsid('');
    setPassword('');
    setStatus(null);
    setError(null);
    setShowPw(true);
    setCountdown(0);
    setPhase('idle');
    setServer('');
    setServerOrig('');
    setReg(null);
    setRegRoom('');
    setRegMsg(null);
    charsRef.current = {};
    completedRef.current = false;
  };

  if (!supported) {
    return (
      <div className="repeater-setup-panel">

        <div className="rs-card rs-err">
          <h2>Web Bluetooth 미지원</h2>
          <p>Chrome 또는 Edge에서 열어주세요. (Safari/Firefox 미지원)</p>
          <button className="rs-btn" onClick={onClose}>닫기</button>
        </div>
      </div>
    );
  }

  return (
    <div className="repeater-setup-panel">
      {qrOpen && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.9)',zIndex:9999,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{color:'#fff',fontSize:16,marginBottom:14}}>공유기 QR 코드를 화면 안에 맞춰주세요</div>
          <div style={{position:'relative',width:'100%',maxWidth:360,aspectRatio:'1',background:'#000',borderRadius:12,overflow:'hidden'}}>
            <video ref={qrVideoRef} playsInline muted autoPlay style={{width:'100%',height:'100%',objectFit:'cover'}} />
            <div style={{position:'absolute',inset:'14%',border:'2px solid #5b8def',borderRadius:8,pointerEvents:'none'}} />
          </div>
          <div style={{color:'#aaa',fontSize:13,marginTop:14,textAlign:'center'}}>{qrHint}</div>
          <button onClick={stopQrScan} style={{marginTop:16,width:'100%',maxWidth:280,padding:14,background:'#fff',color:'#111',border:0,borderRadius:10,fontSize:16,fontWeight:600,cursor:'pointer'}}>취소</button>
          <canvas ref={qrCanvasRef} style={{display:'none'}} />
        </div>
      )}
      <div className="rs-header">
        <h2>중계기 WiFi 설정</h2>
        <span className={`rs-phase rs-phase-${phase}`}>{phaseLabel(phase)}</span>
      </div>

      {phase === 'idle' && (
        <div className="rs-card">
          <p>중계기를 BLE로 검색해서 WiFi를 설정합니다.</p>
          <ul className="rs-steps">
            <li>중계기 전원 ON · BLE 모드 확인</li>
            <li>아래 버튼 클릭 → 시스템 BLE 검색 창에서 중계기 선택</li>
            <li>WiFi SSID 선택 + 비밀번호 입력 (필요 시 서버 주소 변경)</li>
            <li>중계기가 자동으로 WiFi 연결 후 재부팅</li>
          </ul>
          <button className="rs-btn rs-btn-primary" onClick={connect}>
            BLE 검색 시작
          </button>
        </div>
      )}

      {phase === 'connecting' && (
        <div className="rs-card rs-loading">
          <div className="rs-spinner" />
          <p>BLE 연결 중...</p>
        </div>
      )}

      {(phase === 'scanning' || phase === 'ready' || phase === 'sending') && (
        <div className="rs-card">
          {info && (
            <div className="rs-device-info">
              <strong>{info.name || device?.name || '중계기'}</strong>
              {info.mac && <span> · {info.mac}</span>}
              {info.fw && <span> · fw {info.fw}</span>}
            </div>
          )}

          <div className="rs-section">
            <div className="rs-row-between">
              <label>WiFi 목록</label>
              <button className="rs-btn-mini" onClick={triggerScan} disabled={phase === 'scanning'}>
                {phase === 'scanning' ? '스캔 중...' : '🔄 다시 스캔'}
              </button>
            </div>
            {phase === 'scanning' && ssids.length === 0 && (
              <div className="rs-spinner-small" />
            )}
            <div className="rs-ssid-list">
              {ssids.map((s, i) => {
                const ssidStr = typeof s === 'string' ? s : (s.ssid || s.SSID || '');
                const rssi = (typeof s === 'object') ? (s.rssi !== undefined ? s.rssi : (s.RSSI !== undefined ? s.RSSI : -100)) : -100;
                const lvl = rssiToLevel(rssi);
                const sel = ssidStr === ssid;
                return (
                  <button key={i}
                    onClick={() => setSsid(ssidStr)}
                    className={`rs-ssid-btn${sel ? ' rs-ssid-sel' : ''}`}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', cursor:'pointer' }}>
                    <WifiIcon level={lvl} />
                    <span style={{flex:1, textAlign:'left', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{sel && '✓ '}{ssidStr}</span>
                    <span style={{fontSize:11, opacity:0.7, flexShrink:0}}>{rssi} dBm</span>
                  </button>
                );
              })}
            </div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
              <span className="rs-label-mini" style={{ fontSize:12, color:'var(--text-sub)' }}>또는 SSID 직접 입력</span>
              <button type="button" className="rs-btn-mini" onClick={startQrScan}>📷 WiFi QR 스캔</button>
            </div>
            <input
              type="text"
              className="rs-input"
              placeholder="WiFi 이름"
              value={ssid}
              onChange={(e) => setSsid(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
          </div>

          <div className="rs-section">
            <label>WiFi 비밀번호</label>
            <div className="rs-pw-wrap">
              <input
                type={showPw ? 'text' : 'password'}
                className="rs-input"
                placeholder="비밀번호"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
              <button type="button" className={`rs-pw-toggle${showPw ? ' on' : ''}`}
                onClick={() => setShowPw(v => !v)} title="비밀번호 보기">
                {showPw ? (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3.5"/></svg>) : (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3.5"/><line x1="3" y1="3" x2="21" y2="21"/></svg>)}
              </button>
            </div>
          </div>

          <div className="rs-section">
            <label>서버 등록 상태</label>
            {regChecking && <div style={{ color: '#888', fontSize: 13 }}>확인 중...</div>}

            {!regChecking && reg && (
              <div className="rs-same-server">
                ✓ {reg.AREA} / {reg.ROOM_NO} 에 등록되어 있습니다.
                <div style={{ color: '#888', fontSize: 12, marginTop: 4, fontWeight: 400 }}>
                  구역·방번호 변경은 «중계기 관리» 에서 할 수 있습니다.
                </div>
              </div>
            )}

            {!regChecking && reg === false && (
              <div>
                <div className="rs-diff-server" style={{ marginBottom: 8 }}>
                  ⚠ 서버에 등록되지 않은 중계기입니다.
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    className="rs-input"
                    style={{ flex: '0 0 160px' }}
                    value={regArea}
                    onChange={(e) => setRegArea(e.target.value)}>
                    {areas.length === 0 && <option value="">구역 없음</option>}
                    {areas.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                  <input
                    type="text"
                    className="rs-input"
                    style={{ flex: '1 1 140px' }}
                    placeholder="방번호"
                    value={regRoom}
                    onChange={(e) => setRegRoom(e.target.value)}
                    autoComplete="off"
                  />
                  <button type="button" className="rs-btn rs-btn-primary"
                          style={{ flex: '0 0 auto', padding: '10px 18px' }}
                          onClick={registerRepeater} disabled={regBusy}>
                    {regBusy ? '등록 중...' : '등록'}
                  </button>
                </div>
                {regMsg && <div className="rs-error" style={{ marginTop: 8 }}>⚠ {regMsg}</div>}
              </div>
            )}
          </div>

          <div className="rs-section">
            <label>서버 주소 <span style={{ color: '#888', fontWeight: 400 }}>(선택)</span></label>
            <input
              type="text"
              className="rs-input"
              placeholder="http://210.109.15.160:8058"
              value={server}
              onChange={(e) => setServer(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            {(() => {
              const gw = gwFromServer || currentGatewayUrl();
              const typed = normUrl(server);
              const orig = normUrl(serverOrig);

              if (!serverOrig) {
                return (
                  <div style={{ color: '#888', fontSize: 12, marginTop: 6 }}>
                    서버 주소를 확인할 수 없는 펌웨어입니다. 비워 두면 기존 설정이 그대로 유지됩니다.
                  </div>
                );
              }
              // 현재 서버와 같은지 판단할 수 있을 때
              if (gw) {
                const sameNow = orig === normUrl(gw);
                const willChange = typed !== orig;
                if (sameNow && !willChange) {
                  return <div className="rs-same-server">✓ 현재 서버와 같습니다. 변경하지 않아도 됩니다.</div>;
                }
                if (sameNow && willChange) {
                  return <div className="rs-diff-server">⚠ 현재 서버와 다른 곳으로 변경됩니다.</div>;
                }
                if (!sameNow && typed === normUrl(gw)) {
                  return <div className="rs-same-server">✓ 현재 서버로 변경됩니다.</div>;
                }
                return <div className="rs-diff-server">⚠ 다른 서버로 설정되어 있습니다.</div>;
              }
              // 게이트웨이 주소를 확정할 수 없는 경우(도메인 접속 등)
              if (typed !== orig) {
                return <div className="rs-diff-server">⚠ 입력한 주소로 변경됩니다.</div>;
              }
              if (sameHostAsHere(serverOrig)) {
                return <div className="rs-same-server">✓ 지금 접속한 서버와 같은 곳을 보고 있습니다.</div>;
              }
              return (
                <div className="rs-diff-server">
                  ⚠ 지금 접속한 서버와 다른 곳을 보고 있습니다.
                </div>
              );
            })()}

            {(() => {
              // 되돌릴 대상: 서버가 알려준 값 → 접속 IP+포트 → 중계기의 원래 값
              const gw = gwFromServer || currentGatewayUrl();
              const typed = normUrl(server);
              const buttons = [];
              if (gw && typed !== normUrl(gw)) {
                buttons.push(
                  <button key="gw" type="button" className="rs-btn-inline"
                          onClick={() => setServer(gw)}>
                    현재 서버로 변경
                  </button>
                );
              }
              if (serverOrig && typed !== normUrl(serverOrig)) {
                buttons.push(
                  <button key="orig" type="button" className="rs-btn-inline"
                          onClick={() => setServer(serverOrig)}>
                    원래대로
                  </button>
                );
              }
              if (!buttons.length) return null;
              return <div style={{ marginTop: 8 }}>{buttons}</div>;
            })()}
          </div>

          {notice && <div style={{ color: 'var(--accent)', fontSize: 13, marginTop: 8 }}>{notice}</div>}
          {error && <div className="rs-error">⚠ {error}</div>}

          <div className="rs-actions">
            <button className="rs-btn" onClick={disconnect} disabled={phase === 'sending'}>
              취소
            </button>
            <button
              className="rs-btn rs-btn-primary"
              onClick={sendConfig}
              disabled={phase === 'sending' || !ssid}>
              {phase === 'sending' ? '전송 중...' : 'WiFi 설정 전송'}
            </button>
          </div>
        </div>
      )}

      {phase === 'success' && (
        <div className="rs-card rs-ok">
          <div className="rs-big-icon">✓</div>
          <h3>WiFi 설정 완료</h3>
          <p>중계기가 새 설정으로 재부팅 중...</p>
          {countdown > 0 && (
            <p style={{ color: 'var(--accent, #5a8de8)', fontSize: 14, marginTop: 8 }}>
              잠시 후 다음 중계기 설정으로 이동합니다 ({countdown}초)
            </p>
          )}
          <button className="rs-btn rs-btn-primary" onClick={disconnect} style={{ marginTop: 12 }}>
            바로 다음 중계기 설정
          </button>
        </div>
      )}

      {phase === 'failed' && (
        <div className="rs-card rs-err">
          <div className="rs-big-icon">⚠</div>
          <h3>설정 실패</h3>
          <p>{error || 'WiFi 연결에 실패했습니다.'}</p>
          <div className="rs-actions">
            <button className="rs-btn" onClick={disconnect}>처음부터</button>
            <button className="rs-btn rs-btn-primary" onClick={reconnect}>
              다시 시도
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function phaseLabel(p) {
  return {
    idle: '대기',
    connecting: 'BLE 연결 중',
    scanning: 'WiFi 스캔 중',
    ready: '입력 대기',
    sending: '설정 전송',
    success: '완료',
    failed: '실패',
  }[p] || p;
}


/**
 * 원격 서버주소 변경 패널
 *
 * BLE 는 전파 거리 안에서만 되지만 이쪽은 중계기가 서버에 붙어있으면 어디서든 된다.
 * 여기서는 "요청만" 넣고, 실제 전송은 gwserver 가 그 중계기의 다음 보고(최대 31초)
 * 때 한다. 중계기는 새 주소로 전송이 되면 스스로 확정하고, 안 되면 약 2분 뒤
 * 옛 주소로 스스로 되돌아온다(자동 롤백).
 *
 * 1단계라 개별 선택만 둔다. 구역·전체 일괄은 롤백 검증이 더 쌓인 뒤에.
 */
function RemoteConfigPanel() {
  const { dark } = useDarkMode();
  const T = dark ? RC_DARK : RC_LIGHT;
  const [list, setList] = useState([]);
  const [listBusy, setListBusy] = useState(false);
  const [sel, setSel] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);       // {ok:boolean, text:string}
  const [reqs, setReqs] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [includeOffline, setIncludeOffline] = useState(false);
  const [roamingCount, setRoamingCount] = useState(0);
  const [selOpen, setSelOpen] = useState(false);   // 직접 만든 드롭다운 열림 상태

  /** 접속 중인 이 서버의 주소 — 기본값으로 채워준다 */
  const loadDefaultUrl = async () => {
    try {
      const s = await getStorage('Session');
      const ip = s && s.gwIp;
      const port = (typeof process !== 'undefined' && process.env && process.env.GW_PORT) || '';
      if (ip && port) return 'http://' + ip + ':' + port;
    } catch (e) { /* 세션에 없으면 접속 주소로 유추 */ }
    return currentGatewayUrl();
  };

  // silent=true 면 "불러오는 중..." 표시를 건너뛴다.
  // 15초마다 도는 주기갱신에서 그 표시가 깜빡이면 거슬리기 때문이다.
  const loadList = (silent) => {
    if (!silent) setListBusy(true);
    // AREA 를 빈 문자열로 주면 전체가 온다
    Data.fetchData('repeater/getRepeatersList', { AREA: '' }, (result) => {
      setListBusy(false);
      const all = Array.isArray(result) ? result : [];
      // 실제 설치된 중계기가 아닌 것들을 걸러낸다. 그대로 두면 "연결된 N대" 가
      // 부풀고 고를 수도 있게 된다(고르면 당연히 적용이 안 된다).
      //
      // ① AREA 가 '로밍' — 미배치·삭제 중계기가 담기는 칸
      //    실측(10/07): 지점에 로밍이면서 CONNECT=0 으로 뜨는 게 있다
      //    (당진전문 3대, 실버프리 3대, 참사랑 3대, 채운 2대 …)
      // ② ★ID 에 '로밍' 이 박힌 더미 — AREA 는 정상 구역인데 ID 가 가짜다
      //    실측(10/08, MEDI1): 101호로밍 / 1013호로밍 / D0CF13F269C0로밍2·3·4
      //    전부 CONNECT=0(연결됨)이라 AREA 필터만으로는 안 걸러졌다
      // ③ ID 가 'DELETE' 또는 MAC 12자리 형식이 아닌 것 — 삭제 흔적·수기 입력
      //    (중계기 ID 는 WiFi MAC 이라 항상 16진수 12자리다)
      const isRealId = (id) => /^[0-9A-Fa-f]{12}$/.test(String(id || '').trim());
      const rows = all.filter((r) =>
        String(r.AREA || '').indexOf('로밍') === -1
        && String(r.ID || '').indexOf('로밍') === -1
        && isRealId(r.ID));
      rows.sort((a, b) => {
        // 연결된 것 먼저, 그 안에서 구역명 순
        if ((a.CONNECT === 0) !== (b.CONNECT === 0)) return a.CONNECT === 0 ? -1 : 1;
        return String(a.AREA || '').localeCompare(String(b.AREA || ''));
      });
      setRoamingCount(all.length - rows.length);
      setList(rows);
    }, true);
  };

  // 연결 끊긴 중계기는 보고를 안 하므로 SET_CONFIG 를 받을 수 없다 → 기본으로 숨긴다.
  // 다만 "지금 잠깐 끊긴" 중계기에 미리 예약해두는 쓸모가 있어서(재연결되면 받아간다)
  // 체크박스로 열 수 있게 남겨둔다. CONNECT==0 이 '연결됨' 이다(gwsensor 가 alive 때 0 으로 쓴다).
  const online = list.filter((r) => r.CONNECT === 0);
  const offlineCount = list.length - online.length;
  const shown = includeOffline ? list : online;

  const loadReqs = () => {
    Data.fetchData('repeater/getServerUrlRequests', { LIMIT: 30 }, (result) => {
      setReqs(Array.isArray(result) ? result : []);
    }, true);
  };

  useEffect(() => {
    loadList();
    loadReqs();
    (async () => setUrl(await loadDefaultUrl()))();
    // 요청 상태는 중계기 보고(최대 31초)를 기다려야 바뀌고,
    // 연결 상태는 gwserver 가 15초마다 판정한다(임계 90초).
    // 둘 다 사람이 새로고침을 누르지 않아도 따라오게 주기 갱신한다.
    const t = setInterval(() => { loadReqs(); loadList(true); }, 15000);
    return () => clearInterval(t);
  }, []);

  // Data.fetchData 는 callback(result, count) 만 넘긴다(message 없음).
  // 서버가 돌려주는 message 를 쓸 수 없으므로 count 로 문구를 고른다.
  // 서버쪽 count 규약: >0 성공 / -1 대상없음 / -2 주소형식 / -3 저장실패 / -401·-403 권한
  const countMsg = (count) => ({
    '-1': '중계기를 선택해 주세요.',
    '-2': '주소 형식이 올바르지 않습니다. http://IP주소:포트 형식만 됩니다 (도메인·https 불가).',
    '-3': '저장에 실패했습니다.',
    '-401': '로그인이 필요합니다.',
    '-403': '관리자만 사용할 수 있습니다.',
  }[String(count)] || '실패했습니다. (code ' + count + ')');

  const submit = () => {
    setConfirmOpen(false);
    setBusy(true);
    setMsg(null);
    Data.fetchData('repeater/requestServerUrl',
      { RID: sel, SERVER_URL: url.trim() },
      (result, count) => {
        setBusy(false);
        if (count > 0) {
          setMsg({ ok: true, text: '전송 예약했습니다. 중계기의 다음 보고(최대 31초) 때 적용됩니다.' });
          loadReqs();
        } else {
          setMsg({ ok: false, text: countMsg(count) });
        }
      }, true);
  };

  const selected = list.find((r) => r.ID === sel);
  const canSubmit = !!sel && !!url.trim() && !busy;

  const badge = (s) => {
    const color = { 0: '#94A3B8', 1: '#38BDF8', 2: '#16A34A', 3: '#D97706', 8: '#DC2626', 9: '#64748B' }[s] || '#94A3B8';
    return { color, fontWeight: 700 };
  };

  return (
    // 드롭다운 바깥을 누르면 닫힌다 (직접 만든 드롭다운이라 직접 처리해야 한다)
    <div className="repeater-setup-panel" onClick={() => selOpen && setSelOpen(false)}>
      <div className="rs-header"><h2>서버 주소 원격 변경</h2></div>

      <div className="rs-card">
        <p style={{ margin: '0 0 10px', lineHeight: 1.6 }}>
          중계기가 서버에 붙어있으면 현장에 가지 않고 서버 주소를 바꿀 수 있습니다.
          요청하면 그 중계기의 <b>다음 보고(최대 31초)</b> 때 적용됩니다.
          새 주소로 전송이 안 되면 약 <b>2분 뒤 스스로 원래 주소로 되돌아옵니다</b>.
        </p>

        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
          <span className="rs-label-mini">
            중계기 선택 — <span style={{ color: '#16A34A' }}>● 연결 {online.length}대</span>
            {offlineCount > 0 && <span style={{ color: '#94A3B8' }}> / ○ 끊김 {offlineCount}대</span>}
            <span style={{ color: T.muted }}>
              {roamingCount > 0
                ? `  (미배치·더미 ${roamingCount}대 제외 · 15초 자동갱신)`
                : '  (15초 자동갱신)'}
            </span>
          </span>
          {offlineCount > 0 && (
            <label className="rs-label-mini" style={{ cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={includeOffline}
                onChange={(e) => { setIncludeOffline(e.target.checked); setSel(''); setSelOpen(false); }}
                style={{ marginRight: 5, verticalAlign: 'middle' }}
              />
              연결끊김 {offlineCount}대도 보기
            </label>
          )}
        </div>
        {/* 중계기 관리 화면(Repeater.tsx)의 「구역 선택」 드롭다운과 같은 모양.
            네이티브 select 는 appearance:none 을 주면 펼친 목록이 OS 렌더라
            어긋나므로, 그 화면처럼 직접 만든다(박스 + 아래로 펼치는 목록).
            수치도 그쪽 styles.dropdown / dropdownList / dropdownItem 과 맞췄다. */}
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <div
            onClick={(e) => { e.stopPropagation(); if (shown.length) setSelOpen(!selOpen); }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              border: '1px solid ' + T.border, borderRadius: 10, padding: '0 14px', height: 42,
              background: T.cardInner, cursor: shown.length ? 'pointer' : 'not-allowed',
            }}
          >
            <span style={{ fontSize: 14, color: sel ? T.text : T.muted }}>
              {sel
                ? ((selected && selected.AREA) || '구역없음') + ' · ' + sel
                : (listBusy ? '불러오는 중...' : (shown.length ? '중계기를 선택하세요' : '연결된 중계기가 없습니다'))}
            </span>
            <span style={{ color: T.textSub, fontSize: 12 }}>{selOpen ? '▲' : '▼'}</span>
          </div>
          {selOpen && (
            <div style={{
              position: 'absolute', left: 0, right: 0, top: 46, zIndex: 50,
              border: '1px solid ' + T.border, borderRadius: 10, marginTop: 4,
              background: T.card, overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
            }}>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {shown.map((r) => {
                  const on = r.ID === sel;
                  return (
                    <div
                      key={r.ID}
                      onClick={(e) => { e.stopPropagation(); setSel(r.ID); setSelOpen(false); }}
                      style={{
                        padding: '10px 14px', borderBottom: '1px solid ' + T.border,
                        background: on ? 'rgba(56,189,248,0.07)' : 'transparent',
                        cursor: 'pointer', fontSize: 13,
                        fontWeight: on ? 700 : 500,
                        color: on ? RC_ACCENT : T.text,
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}
                    >
                      <span style={{ color: r.CONNECT === 0 ? '#16A34A' : T.muted, fontSize: 11 }}>
                        {r.CONNECT === 0 ? '●' : '○'}
                      </span>
                      <span>{(r.AREA || '구역없음') + ' · ' + r.ID}</span>
                      {r.CONNECT !== 0 && (
                        <span style={{ color: T.muted, fontSize: 11 }}>(연결끊김)</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {selected && selected.CONNECT !== 0 && (
          <div className="rs-diff-server" style={{ marginBottom: 10 }}>
            ⚠ 이 중계기는 지금 연결이 끊겨 있습니다. <b>다시 붙을 때까지 적용되지 않고 대기</b>합니다.
          </div>
        )}

        <div className="rs-label-mini" style={{ marginBottom: 4 }}>새 서버 주소</div>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="http://210.109.15.160:8058"
          spellCheck={false}
          style={{
            width: '100%', padding: 10, boxSizing: 'border-box', height: 42,
            borderRadius: 10, fontSize: 14, fontFamily: 'monospace',
            border: '1px solid ' + T.border, background: T.cardInner,
            color: T.text, outline: 'none',
          }}
        />
        <div className="rs-label-mini" style={{ marginTop: 6, lineHeight: 1.5 }}>
          <b>http://IP주소:포트</b> 형식만 됩니다. 도메인·https·포트 생략은 중계기가 거부합니다.
          기본값은 지금 접속 중인 서버 주소입니다.
        </div>

        {/* rs-btn-primary 는 #38BDF8 배경에 흰 글씨라 명암비가 2:1 수준이다.
            여기서는 같은 하늘색에 짙은 글씨를 써서 또렷하게 만든다. */}
        <button
          disabled={!canSubmit}
          onClick={() => setConfirmOpen(true)}
          style={{
            marginTop: 14, width: '100%', padding: '14px 16px', minHeight: 50,
            borderRadius: 8, border: 0, cursor: canSubmit ? 'pointer' : 'not-allowed',
            background: canSubmit ? RC_ACCENT : T.cardInner,
            color: canSubmit ? '#0B1220' : T.muted,
            fontSize: 16, fontWeight: 800,
          }}
        >
          {busy ? '요청 중...' : '원격 변경 요청'}
        </button>

        {msg && (
          <div className={msg.ok ? 'rs-same-server' : 'rs-diff-server'} style={{ marginTop: 10 }}>
            {msg.text}
          </div>
        )}
      </div>

      {confirmOpen && (
        <div
          onClick={() => setConfirmOpen(false)}       /* 바깥 클릭으로 닫기 */
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 440, width: '100%', maxHeight: '85vh', overflowY: 'auto',
              background: T.card, border: '1px solid ' + T.border, borderRadius: 12,
              padding: 20, boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
            }}
          >
            <h2 style={{ margin: '0 0 12px', color: T.text, fontSize: 18 }}>서버 주소를 바꿉니다</h2>
            <div style={{ background: T.cardInner, borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ color: T.textSub, fontSize: 12, marginBottom: 4 }}>대상 중계기</div>
              <div style={{ color: T.text, fontWeight: 700, marginBottom: 10 }}>
                {(selected && selected.AREA) || '구역없음'} / <span style={{ fontFamily: 'monospace' }}>{sel}</span>
              </div>
              <div style={{ color: T.textSub, fontSize: 12, marginBottom: 4 }}>새 서버 주소</div>
              <div style={{ color: RC_ACCENT, fontWeight: 700, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {url.trim()}
              </div>
            </div>
            <p style={{ color: T.textSub, fontSize: 13, lineHeight: 1.7, margin: '0 0 16px' }}>
              주소가 잘못되면 그 중계기는 약 2분간 데이터를 보내지 못하다가
              <b style={{ color: T.text }}> 스스로 원래 주소로 되돌아옵니다.</b>
            </p>
            {/* 두 버튼을 또렷하게 구분한다 — rs-btn-primary(하늘색+흰글씨)는 명암비가 낮다 */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setConfirmOpen(false)}
                style={{
                  flex: 1, padding: '14px 16px', minHeight: 50, borderRadius: 8,
                  border: '1px solid ' + T.border, background: 'transparent',
                  color: T.text, fontSize: 15, fontWeight: 600, cursor: 'pointer',
                }}
              >취소</button>
              <button
                onClick={submit}
                autoFocus
                style={{
                  flex: 1.4, padding: '14px 16px', minHeight: 50, borderRadius: 8,
                  border: 0, background: RC_ACCENT, color: '#0B1220',
                  fontSize: 16, fontWeight: 800, cursor: 'pointer',
                }}
              >변경하기</button>
            </div>
          </div>
        </div>
      )}

      <div className="rs-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>요청 내역</h2>
          <button className="rs-btn-inline" onClick={loadReqs}>새로고침</button>
        </div>
        {!reqs.length && <p className="rs-label-mini" style={{ marginTop: 10 }}>요청 내역이 없습니다.</p>}
        {!!reqs.length && (
          <div style={{ overflowX: 'auto', marginTop: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-sub)' }}>
                  <th style={{ padding: '6px 8px' }}>중계기</th>
                  <th style={{ padding: '6px 8px' }}>주소</th>
                  <th style={{ padding: '6px 8px' }}>상태</th>
                  <th style={{ padding: '6px 8px' }}>요청</th>
                  <th style={{ padding: '6px 8px' }} />
                </tr>
              </thead>
              <tbody>
                {reqs.map((r) => (
                  <tr key={r.IDX} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.REPEATER_ID}</td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.SERVER_URL}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <span style={badge(r.STATUS)}>{r.STATUS_TEXT}</span>
                      {r.FAIL_REASON ? <div className="rs-label-mini">{r.FAIL_REASON}</div> : null}
                    </td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                      {String(r.REGDATE || '').replace('T', ' ').slice(0, 16)}
                      {r.REQ_USER ? <div className="rs-label-mini">{r.REQ_USER}</div> : null}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {r.STATUS === 0 && (
                        <button
                          className="rs-btn-inline"
                          onClick={() => Data.fetchData('repeater/cancelServerUrlRequest', { IDX: r.IDX },
                            (result, count) => {
                              if (count > 0) setMsg({ ok: true, text: '취소했습니다.' });
                              else if (count === 0) setMsg({ ok: false, text: '이미 전송된 요청은 취소할 수 없습니다.' });
                              else setMsg({ ok: false, text: countMsg(count) });
                              loadReqs();
                            }, true)}
                        >취소</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="rs-label-mini" style={{ marginTop: 10, lineHeight: 1.6 }}>
          대기 → 전송됨 → (성공) 적용확인 / (실패) 롤백됨.
          <b> 전송됨</b> 이후에는 취소할 수 없습니다 — 중계기가 이미 받았으므로 스스로 확정하거나 되돌립니다.
        </div>
      </div>
    </div>
  );
}


/** 관리자웹 화면 래퍼 — 상단 여백과 뒤로가기를 붙인다 */
export default function RepeaterSetup() {
  const navigation = useNavigation();
  // BLE 직접 / 원격 두 방식을 탭으로 나눈다.
  // 탭을 래퍼에 둔 이유: BLE 패널은 Web Bluetooth 미지원이면 통째로 막히는데,
  // 원격 탭은 Bluetooth 가 필요 없으므로 Safari 에서도 쓸 수 있어야 한다.
  const [tab, setTab] = useState('ble');
  const tabBtn = (key, label) => (
    <button
      key={key}
      onClick={() => setTab(key)}
      style={{
        flex: 1, padding: '10px 8px', cursor: 'pointer', fontSize: 14, fontWeight: 700,
        background: 'transparent', border: 0,
        color: tab === key ? '#38BDF8' : '#94A3B8',
        borderBottom: tab === key ? '2px solid #38BDF8' : '2px solid rgba(148,163,184,0.25)',
      }}
    >{label}</button>
  );
  return (
    <div style={{ height: '100%', overflowY: 'auto', background: 'transparent' }}>
      <style>{RS_CSS}</style>
      <div style={{ display: 'flex', margin: '0 0 4px' }}>
        {tabBtn('ble', 'BLE 직접 설정')}
        {tabBtn('remote', '서버주소 원격 변경')}
      </div>
      {tab === 'ble'
        ? <RepeaterSetupPanel onClose={() => navigation.goBack()} />
        : <RemoteConfigPanel />}
    </div>
  );
}
